<!-- src\components\admin\dashboard\ActivityChart.vue -->
<template>
	<div class="admn_activity-chart">
		<div class="admn_chart-header">
			<h2 class="admn_chart-title">Активность системы</h2>
			<div class="admn_chart-actions">
				<select v-model="selectedPeriod" class="admn_period-select" @change="fetchData">
					<option value="year">За год</option>
					<option value="month">За месяц</option>
					<option value="week">За неделю</option>
				</select>
			</div>
		</div>
		
		<div class="admn_chart-container">
			<div v-if="loading" class="admn_chart-loading">
				<div class="spinner"></div>
			</div>
			<div v-else-if="error" class="admn_chart-error">
				<span>{{ error }}</span>
				<button @click="fetchData" class="retry-btn">Повторить</button>
			</div>
			<Line v-else :data="chartData" :options="chartOptions" />
		</div>
		
		<div class="admn_chart-legend">
			<div class="admn_legend-item">
				<span class="admn_legend-dot admn_legend-dot--primary"></span>
				<span>Активные пользователи</span>
			</div>
			<div class="admn_legend-item">
				<span class="admn_legend-dot admn_legend-dot--secondary"></span>
				<span>Новые регистрации</span>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Line } from 'vue-chartjs'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
} from 'chart.js'
import { getActivityChart } from '@/services/dashboard'

// Регистрация компонентов Chart.js
ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler
)

const selectedPeriod = ref('month')
const loading = ref(true)
const error = ref(null)
const chartDataRaw = ref({ labels: [], datasets: [] })

// Маппинг английских месяцев на русские
const monthsMap = {
	Jan: 'Янв', Feb: 'Фев', Mar: 'Мар', Apr: 'Апр',
	May: 'Май', Jun: 'Июн', Jul: 'Июл', Aug: 'Авг',
	Sep: 'Сен', Oct: 'Окт', Nov: 'Ноя', Dec: 'Дек',
}

const chartData = computed(() => ({
	labels: chartDataRaw.value.labels.map(l => monthsMap[l] || l),
	datasets: chartDataRaw.value.datasets.map(ds => ({
		...ds,
		pointRadius: 3,
		pointHoverRadius: 5,
		borderWidth: 2,
	})),
}))

const chartOptions = computed(() => ({
	responsive: true,
	maintainAspectRatio: false,
	interaction: {
		mode: 'index',
		intersect: false,
	},
	plugins: {
		legend: { display: false },
		tooltip: {
			backgroundColor: 'rgba(0, 0, 0, 0.8)',
			titleFont: { family: 'Poppins', size: 13 },
			bodyFont: { family: 'Poppins', size: 12 },
			padding: 12,
			cornerRadius: 8,
			callbacks: {
				label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}`,
			},
		},
	},
	scales: {
		x: {
			grid: { display: false },
			ticks: {
				font: { family: 'Poppins', size: 11 },
				color: 'rgba(0, 0, 0, 0.5)',
				maxRotation: 0,
			},
		},
		y: {
			beginAtZero: true,
			grid: { color: '#f0f0f0' },
			ticks: {
				font: { family: 'Poppins', size: 11 },
				color: 'rgba(0, 0, 0, 0.5)',
				stepSize: selectedPeriod.value === 'year' ? undefined : 1,
			},
		},
	},
}))

const fetchData = async () => {
	try {
		loading.value = true
		error.value = null
		const response = await getActivityChart({ period: selectedPeriod.value })
		const data = response.data || response
		chartDataRaw.value = data
	} catch (err) {
		console.error('[ActivityChart] Ошибка:', err)
		error.value = 'Не удалось загрузить данные'
	} finally {
		loading.value = false
	}
}

onMounted(fetchData)
</script>

<style scoped>
.admn_activity-chart {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 16px 20px;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	min-width: 0;
}

.admn_chart-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	flex-shrink: 0;
}

.admn_chart-title {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 16px;
	font-weight: 600;
	color: #1a1a1a;
	margin: 0;
}

.admn_chart-actions {
	display: flex;
	gap: 12px;
}

.admn_period-select {
	padding: 6px 12px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 13px;
	background: #ffffff;
	cursor: pointer;
	transition: border-color 0.2s ease;
}

.admn_period-select:hover {
	border-color: #2563ea;
}

.admn_period-select:focus {
	outline: none;
	border-color: #2563ea;
	box-shadow: 0 0 0 3px rgba(37, 99, 234, 0.1);
}

.admn_chart-container {
	flex: 1;
	min-height: 0;
	position: relative;
}

.admn_chart-loading,
.admn_chart-error {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	background: #fafafa;
	border-radius: 8px;
}

.admn_chart-error {
	color: #666;
	font-size: 14px;
}

.retry-btn {
	padding: 6px 16px;
	background: #2563ea;
	color: white;
	border: none;
	border-radius: 6px;
	font-size: 13px;
	cursor: pointer;
	transition: background 0.2s;
}

.retry-btn:hover {
	background: #1d4ed8;
}

.spinner {
	width: 32px;
	height: 32px;
	border: 3px solid #e0e0e0;
	border-top-color: #2563ea;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to { transform: rotate(360deg); }
}

.admn_chart-legend {
	display: flex;
	justify-content: center;
	gap: 24px;
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px solid #f0f0f0;
	flex-shrink: 0;
}

.admn_legend-item {
	display: flex;
	align-items: center;
	gap: 6px;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 12px;
	color: #666;
}

.admn_legend-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
}

.admn_legend-dot--primary {
	background: #2563ea;
}

.admn_legend-dot--secondary {
	background: #22c55e;
}

@media (max-width: 768px) {
	.admn_chart-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
	}
	
	.admn_chart-legend {
		flex-direction: column;
		align-items: center;
		gap: 8px;
	}
}
/* ===== АДАПТИВНОЕ МАСШТАБИРОВАНИЕ ПОД РАЗНЫЕ РАЗРЕШЕНИЯ И МАСШТАБЫ БРАУЗЕРА ===== */
/* Дизайн создан для 1920px, поэтому нужно масштабирование для меньших разрешений */

/* Экраны ≤1366px (реальные мониторы 1366×768) */
@media (max-width: 1366px) {
	body {
		zoom: 0.711; /* 1366 ÷ 1920 = 0.711 */
	}

	/* Убираем центрирование и ограничиваем ширину контейнеров */
	.container-center-horizontal {
		justify-content: flex-start !important;
	}

	.u1075u1083u1072u1074u1085u1072u1103-u1074u1072u1088u1080u1072u1085u1090-9,
	.x7 {
		max-width: 100% !important;
	}
}

/* Экраны 1367-1600px (включает FullHD с масштабом 125%: 1920÷1.25=1536px) */
@media (min-width: 1367px) and (max-width: 1600px) {
	body {
		zoom: 0.85; /* Компромисс между 0.711 и 1.0 для промежуточных разрешений */
	}

	/* Убираем центрирование и ограничиваем ширину контейнеров */
	.container-center-horizontal {
		justify-content: flex-start !important;
	}

	.u1075u1083u1072u1074u1085u1072u1103-u1074u1072u1088u1080u1072u1085u1090-9,
	.x7 {
		max-width: 100% !important;
	}
}

/* QHD 2560x1440 (15.6") — компенсация под 100% масштаб (как при 90%) */
/* При масштабе Windows 150% браузер видит ~1707px (2560/1.5) */
@media (min-width: 1601px) and (max-width: 1800px) {
	body {
		zoom: 0.9 !important;
	}
}

/* Экраны 1801-2300px - без дополнительного масштабирования */
@media (min-width: 1801px) and (max-width: 2300px) {
	body {
		zoom: 1.0 !important;
	}
}

/* QHD 2560x1440 с масштабом Windows 100% - увеличение до 150% */
/* При масштабе Windows 100% браузер видит полные 2560px */
@media (min-width: 2301px) and (max-width: 2880px) {
	body {
		zoom: 1.35 !important;
	}
}

</style>


